import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { FadeIn, Eyebrow, MaskedLine } from "@/components/Reveal";

const BASE_URL = "https://www.contabeleza.com.br";

const TRACKS = [
    {
        id: "saloes",
        title: "Salões e Barbearias",
        img: "https://images.unsplash.com/photo-1633681926022-84c23e8cb2d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxiZWF1dHklMjBzYWxvbiUyMGVudHJlcHJlbmV1cnxlbnwwfHx8fDE3ODg2MTE0OTN8MA&ixlib=rb-4.1.0&q=85",
        pains: [
            "Profissionais sem contrato e medo de ação trabalhista",
            "Comissões calculadas na mão todo fim de semana",
            "Imposto pago sobre faturamento que nem é do salão",
        ],
        gains: [
            "Contratos de parceria adequados à Lei nº 13.352/2016",
            "Comissões e divisão de cotas acompanhadas no ilove.me",
            "Orientação para notas discriminadas: cota-salão e cota-parceiro",
        ],
    },
    {
        id: "clinicas",
        title: "Clínicas de Estética e Spas",
        img: "https://images.pexels.com/photos/16571735/pexels-photo-16571735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        pains: [
            "Dúvida sobre enquadramento fiscal de serviços de estética",
            "Mistura de CLT, parceiros e locação de sala sem regra clara",
            "Financeiro e contabilidade que não conversam entre si",
        ],
        gains: [
            "Planejamento tributário específico para estética",
            "Orientação sobre modelos de contratação para cada profissional",
            "BPO financeiro com relatórios de lucratividade por serviço",
        ],
    },
    {
        id: "profissionais",
        title: "Profissionais da Beleza",
        img: "https://images.unsplash.com/photo-1695527081848-1e46c06e6458?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBoYWlyJTIwc2Fsb24lMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODg2MTE0OTN8MA&ixlib=rb-4.1.0&q=85",
        pains: [
            "Informalidade que trava financiamento e crescimento",
            "Não sabe se vale MEI, ME ou ser parceiro de salão",
            "Medo de assinar contrato sem entender os direitos",
        ],
        gains: [
            "CNPJ aberto no enquadramento certo para a sua realidade",
            "Contrato de parceria adequado à lei, com orientação para você e o salão",
            "Rotina fiscal simples, explicada sem contabilês",
        ],
    },
];

export default function AudiencePage() {
    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Início", item: `${BASE_URL}/` },
                    { "@type": "ListItem", position: 2, name: "Para quem", item: `${BASE_URL}/para-quem` },
                ],
            },
            {
                "@type": "ItemList",
                name: "Trilhas da Contabeleza por perfil de negócio",
                itemListElement: TRACKS.map((track, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    item: {
                        "@type": "Service",
                        name: track.title,
                        description: track.gains.join(" "),
                        provider: { "@type": "Organization", name: "Contabeleza", url: `${BASE_URL}/` },
                        areaServed: "BR",
                    },
                })),
            },
        ],
    };

    return (
        <div className="page-bg-para-quem">
            <Seo
                title="Para Quem, Contabeleza | Salões, clínicas de estética e profissionais da beleza"
                description="Trilhas dedicadas para salões e barbearias, clínicas de estética e profissionais autônomos da beleza. Segurança jurídica, tributos certos e tecnologia integrada."
                jsonLd={jsonLd}
            />

            <section className="page-hero-para-quem relative overflow-hidden px-5 pb-20 pt-32 sm:px-8 sm:pt-40 sm:pb-28">
                <div className="relative mx-auto max-w-7xl text-center">
                    <FadeIn y={16}>
                        <Eyebrow color="text-[#FFE1A8]">Para quem</Eyebrow>
                    </FadeIn>
                    <h1 className="mx-auto mt-5 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight text-[#F3EDE6] sm:text-5xl lg:text-6xl">
                        <MaskedLine delay={0.1}>A gente fala a língua</MaskedLine>
                        <MaskedLine delay={0.25}>
                            <span className="text-[#FFE1A8]">do seu negócio.</span>
                        </MaskedLine>
                    </h1>
                    <FadeIn delay={0.45} className="mx-auto mt-6 max-w-xl">
                        <p className="text-base leading-relaxed text-white/85 sm:text-lg">
                            Salão de bairro, barbearia premium, clínica de estética ou cadeira alugada: cada
                            operação tem suas dores, e a gente conhece todas pelo nome.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="px-5 py-20 sm:px-8 sm:py-28">
                <div className="mx-auto max-w-7xl space-y-20">
                    {TRACKS.map((track, i) => (
                        <FadeIn key={track.id}>
                            <article
                                id={track.id}
                                data-testid={`audience-track-${track.id}`}
                                className={`grid scroll-mt-28 items-center gap-10 lg:grid-cols-2 ${
                                    i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                                }`}
                            >
                                <div className="gota-frame aspect-[4/3] overflow-hidden border border-[#08104F]/10 shadow-[0_30px_70px_rgba(8,16,79,0.14)]">
                                    <img
                                        src={track.img}
                                        alt={track.title}
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                                <div>
                                    <span className="font-display text-5xl text-[#08104F]/10">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <h2 className="mt-3 font-display text-2xl leading-tight text-[#08104F] sm:text-3xl">
                                        {track.title}
                                    </h2>
                                    <div className="mt-7 grid gap-6 sm:grid-cols-2">
                                        <div className="rounded-2xl border border-[#F35E6A]/20 bg-[#F35E6A]/5 p-5">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F35E6A]">
                                                O que trava você
                                            </p>
                                            <ul className="mt-3.5 space-y-2.5">
                                                {track.pains.map((pain) => (
                                                    <li key={pain} className="text-xs leading-relaxed text-[#3A4768]">
                                                        {pain}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="rounded-2xl border border-[#0047BB]/20 bg-[#B2D0FF]/15 p-5">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0047BB]">
                                                Como destravamos
                                            </p>
                                            <ul className="mt-3.5 space-y-2.5">
                                                {track.gains.map((gain) => (
                                                    <li key={gain} className="flex gap-2 text-xs leading-relaxed text-[#3A4768]">
                                                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#0047BB]" />
                                                        {gain}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                    <Link
                                        to="/contato"
                                        data-testid={`btn-audience-cta-${track.id}`}
                                        className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#0047BB]"
                                    >
                                        Quero essa trilha no meu negócio
                                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                                    </Link>
                                </div>
                            </article>
                        </FadeIn>
                    ))}
                </div>
            </section>

            <CtaBand
                title="Não se encaixou em nenhuma trilha?"
                text="Do estúdio de tatuagem à rede de franquias: se é beleza, a gente entende. Chame um especialista e conte o seu caso."
            />
        </div>
    );
}
